import { z } from 'zod/v4';

import { AppType, ContentSecurityPolicyFlavor } from './constants';

// TODO (Julia Wang): Delete the default `us` region once we've added the REGION
// env var to all services. (We'll get that for free by migrating services to
// Terraform.)
export const Regions = ['us', 'eu'] as const;
export const RegionEnum = z.enum(Regions);
export const RegionSchema = RegionEnum.default('us');
// This implemented as typeof Regions instead of z.infer<typeof RegionEnum>
// to avoid a bug in ts-json-schema-generator that causes to crash when
// generating workflow schemas when this type is used.
export type Region = (typeof Regions)[number];

export const EnvironmentSchema = z.enum(['test', 'production']);
export type Environment = z.infer<typeof EnvironmentSchema>;

export const CloudProviderEnum = z.enum(['gcp', 'render']);
export const CloudProviderSchema = CloudProviderEnum.default('render');
export type CloudProvider = z.infer<typeof CloudProviderEnum>;

// NOTE(wc): there are many settings here that are too
// web-app specific and we should likely factor them out.
export interface ApplicationConfig {
  APPLICATION_NAME: string;
  APPLICATION_VERSION: string;
  SKIP_NEXT?: boolean;
  PORT: number;
  STATIC_PATH: string;
  NEXT_STATIC_PATH: string;
  ENFORCE_ORG_SCOPING: boolean;
  SESSION_KEY: string;
  OLD_SESSION_KEY?: string; // Used for signing secret rotation
  READONLY_SESSION_KEY?: string; // Used for signing secret rotation
  SESSION_COOKIE_NAME: string;
  SESSION_COOKIE_DOMAIN?: string;
  HELPJUICE_API_KEY?: string;
  MAX_SESSION_AGE_HOURS?: number;
  CSP_FLAVOR?: ContentSecurityPolicyFlavor;
}

export interface PostgresConfig {
  // Regional primary database
  DB_HOST: string;
  DB_NAME: string;
  DB_USER: string;
  DB_PASSWORD: string;

  // Regional read replica
  DB_REPLICA_HOST: string;
  DB_REPLICA_NAME: string;
  DB_REPLICA_PASSWORD: string;
  DB_REPLICA_USER: string;

  // Global primary database
  GLOBAL_DB_HOST: string;
  GLOBAL_DB_NAME: string;
  GLOBAL_DB_PASSWORD: string;
  GLOBAL_DB_USER: string;

  // Global read replica
  GLOBAL_DB_REPLICA_HOST: string;
  GLOBAL_DB_REPLICA_NAME: string;
  GLOBAL_DB_REPLICA_PASSWORD: string;
  GLOBAL_DB_REPLICA_USER: string;

  // Connection settings (shared across all databases)
  DB_USE_SSL: boolean;
  DB_ALLOW_SELF_SIGNED_CERTIFICATES: boolean;

  /**
   * Database connection pool settings.
   *
   * DB_POOL_IDLE_TIMEOUT_MS and DB_POOL_REAP_INTERVAL_MS work together to
   * automatically clean up idle connections - the reap interval determines
   * how often to check for idle connections, and the idle timeout determines how long a connection can
   * sit unused before being destroyed. This will reduce the active pool size
   * down to the DB_POOL_MIN value when connections aren't needed.
   * */
  DB_POOL_MIN: number;
  DB_POOL_MAX: number;
  DB_POOL_IDLE_TIMEOUT_MS: number;
  DB_POOL_REAP_INTERVAL_MS: number;
}

const GoogleStorageReplicationPathSchema = z.object({
  bucket: z.string(),
  path: z.string().refine((x) => x.endsWith('/') || x === ''),
});

export const GoogleStorageReplicationConfigSchema = z.array(
  z.object({
    primary: GoogleStorageReplicationPathSchema,
    replica: GoogleStorageReplicationPathSchema,
  })
);

export type GoogleStorageReplicationConfig = z.infer<
  typeof GoogleStorageReplicationConfigSchema
>;
export interface GoogleStorageConfig {
  // https://www.notion.so/GCS-Buckets-d77901fac4e040a3a1f2f8709b52f235
  GCP_STORAGE_BUCKET_ADMIN: string;
  GCP_STORAGE_BUCKET_DASHBOARD: string;
  GCP_STORAGE_BUCKET_DASHBOARD_COMMUNAL: string;
  GCP_STORAGE_BUCKET_UPLOADS: string;
  GCP_STORAGE_BUCKET_GLOBAL: string;
  GCP_STORAGE_BUCKET_PUBLIC: string;
  GCP_STORAGE_BUCKET_MEASUREMENT: string;
  GCP_STORAGE_BUCKET_MEASUREMENT_COMMUNAL: string;
  GCP_STORAGE_BUCKET_FIXTURES: string;
  GCP_STORAGE_BUCKET_TRACE: string;
  GCP_STORAGE_BUCKET_S_AND_P: string;
  GCP_STORAGE_BUCKET_SUPPORT: string;
  GCP_STORAGE_BUCKET_ORG_DELETION_ARTIFACTS: string;
  GCP_STORAGE_BUCKET_PRODUCTION_GRAPH: string;
  GCP_STORAGE_BUCKET_TEMPORAL_ARTIFACTS: string;

  GCP_USER_UPLOADS_PUBSUB: string;

  replicationConfig: GoogleStorageReplicationConfig;
}

export interface TemporalClientConfig {
  TEMPORAL_ADDRESS: string;
  TEMPORAL_WEB_ADDRESS: string;
  TEMPORAL_NAMESPACE_ID: string;
  tls?: {
    clientCertPair: {
      crt: Buffer;
      key: Buffer;
    };
  };
}

export interface WorkOsConfig {
  WORKOS_API_KEY: string;
  WORKOS_CLIENT_ID: string;
  WORKOS_WEBHOOK_SECRET: string;
}

export interface QuickbooksConfig {
  QUICKBOOKS_CLIENT_ID: string;
  QUICKBOOKS_CLIENT_SECRET: string;
  QUICKBOOKS_ENV: string;
  QUICKBOOKS_API_URL: string;
}

export interface MergeConfig {
  MERGE_API_KEY: string;
}

// OneSchema template
export interface OneSchemaConfig {
  ONE_SCHEMA_VALIDATION_WEBHOOK_SECRET: string;
  ONE_SCHEMA_EVENTS_WEBHOOK_SECRET: string;
  ONE_SCHEMA_FILEFEED_EVENTS_WEBHOOK_SECRET: string;
  ONE_SCHEMA_EXTERNAL_API_KEY: string;
  ONE_SCHEMA_CLIENT_ID: string;
  ONE_SCHEMA_CLIENT_SECRET: string;
}

export interface FlexibleReportsConfig {
  // Current signing key (required) - used for signing new JWTs
  FLEXIBLE_REPORTS_JWT_SECRET_CURRENT: string;
  FLEXIBLE_REPORTS_JWT_PUBLIC_KEY_CURRENT: string;
  // Previous key for verification during/after rotation (optional)
  FLEXIBLE_REPORTS_JWT_SECRET_PREVIOUS?: string;
  FLEXIBLE_REPORTS_JWT_PUBLIC_KEY_PREVIOUS?: string;
  // Next key pre-staged for upcoming rotation (optional)
  FLEXIBLE_REPORTS_JWT_SECRET_NEXT?: string;
  FLEXIBLE_REPORTS_JWT_PUBLIC_KEY_NEXT?: string;
  // Legacy keys (deprecated, for backward compat - used as fallback if _CURRENT is not set)
  FLEXIBLE_REPORTS_JWT_SECRET: string;
  FLEXIBLE_REPORTS_JWT_PUBLIC_KEY: string;
}

export interface SendgridConfig {
  SENDGRID_API_KEY: string;
  SENDGRID_EVENTS_WEBHOOK_VERIFICATION_KEY?: string;
}

export interface SalesforceConfig {
  SALESFORCE_CLIENT_ID: string;
  SALESFORCE_CLIENT_SECRET: string;
  SALESFORCE_SANDBOX_CLIENT_ID: string;
  SALESFORCE_SANDBOX_CLIENT_SECRET: string;
  SALESFORCE_AUTOPROV_SANDBOX_CLIENT_ID: string;
  SALESFORCE_AUTOPROV_SANDBOX_CLIENT_SECRET: string;
  SALESFORCE_ENABLE_PUBSUB_TO_PRODUCTION: boolean;
}

export interface OpalConfig {
  OPAL_SIGNING_SECRET: string;
  OPAL_SIGNING_SECRET_EU: string;
  OPAL_ACCESS_TOKEN: string;
}

export enum HoneycombMode {
  None = 'none',
  DemoPlatformOnly = 'demo-platform-only',
  All = 'all',
}

export interface HoneycombConfig {
  HONEYCOMB_MODE: HoneycombMode;
  HONEYCOMB_KEY: string;
  OTEL_COLLECTOR_URL: string;
}

export interface CdpApiConfig {
  CDP_API_URL: string;
  CDP_API_KEY: string;
}

export interface DuckDbServerConfig {
  DUCKDB_SERVER_URL: string;
  DUCKDB_SERVER_EXTERNAL_URL: string;
  DUCKDB_SERVER_EXTRA_LARGE_URL: string;
  DUCKDB_SERVER_COMPUTE_URL: string;
  DUCKDB_SERVER_POSTGRES_URL: string;
  DUCKDB_SERVER_TEST_URL: string;
  DUCKDB_SERVER_TEST_EXTRA_LARGE_URL: string;
  DUCKDB_SERVER_LOCAL_URL: string;
}

export interface DuckDbInProcessConfig {
  DUCKDB_MEMORY_LIMIT_GB: number;
  DUCKDB_PARQUET_WRITE_THREADS: number;
  ENABLE_DUCKDB_SPATIAL_EXTENSION: boolean;
}

export interface DuckDbTemporalConfig {
  DUCKDB_QUEUE_NAME_10GI: string;
  DUCKDB_QUEUE_NAME_54GI: string;
  DUCKDB_QUEUE_NAME_234GI: string;
  DUCKDB_QUEUE_NAME_355GI: string;
  DUCKDB_QUEUE_NAME_719GI: string;
  DUCKDB_QUEUE_NAME_1448GI: string;
  DUCKDB_QUEUE_NAME_WF: string;
}

export interface DuckDbElasticConfig {
  DUCKDB_WATCHDOG_ENABLE_RESOURCE_MONITORING: boolean;
  DUCKDB_WATCHDOG_ENABLE_ALERTS: boolean;
  DUCKDB_WATCHDOG_ENFORCE_LIMITS: boolean;
  DUCKDB_WATCHDOG_NEAR_LIMIT_THRESHOLD: number;
  DUCKDB_WATCHDOG_TERMINATION_GRACE_PERIOD_SECONDS: number;
  DUCKDB_WATCHDOG_MEMORY_LIMIT_GB: number;
  DUCKDB_WATCHDOG_DISK_LIMIT_GB: number;
}

export interface MapboxConfig {
  MAPBOX_API_KEY: string;
}

export enum QueryGovernance {
  None,
  Warn,
  Error,
}

export enum CrossDatabaseTransactionGovernance {
  None,
  Warn,
  Error,
}

export enum CrossRegionReadGovernance {
  Warn,
  Error,
}

export interface CrowdInConfig {
  CROWDIN_API_KEY: string;
  CROWDIN_PROJECT_ID: string;
  CROWDIN_DYNAMIC_INCOMING_DIRECTORY_ID: string;
  CROWDIN_DYNAMIC_STRING_BUNDLE_ID: string;
  CROWDIN_STATIC_STRING_BUNDLE_ID: string;
}

export interface SentryConfig {
  SENTRY_DSN: string;
  SENTRY_ENABLED: boolean;
  SENTRY_TOKEN: string;
  SENTRY_ENV: string;
}

export interface HelpJuiceConfig {
  HELPJUICE_API_KEY: string;
  HELPJUICE_ORG_NAME: string;
}

export interface LangfuseConfig {
  LANGFUSE_SANDBOX_SECRET_KEY: string;
  LANGFUSE_SANDBOX_PUBLIC_KEY: string;
  LANGFUSE_DATA_INGESTION_SECRET_KEY: string;
  LANGFUSE_DATA_INGESTION_PUBLIC_KEY: string;
  LANGFUSE_BASE_URL: string;
  LANGFUSE_ENABLED: boolean;
  LANGFUSE_TEST_PROJECT_SECRET_KEY: string;
  LANGFUSE_TEST_PROJECT_PUBLIC_KEY: string;
  LANGFUSE_REPORTING_ANSWER_SECRET_KEY: string;
  LANGFUSE_REPORTING_ANSWER_PUBLIC_KEY: string;
  LANGFUSE_PRODUCTION_GRAPH_SECRET_KEY: string;
  LANGFUSE_PRODUCTION_GRAPH_PUBLIC_KEY: string;
  LANGFUSE_LABS_AGENT_SECRET_KEY: string;
  LANGFUSE_LABS_AGENT_PUBLIC_KEY: string;
}

export interface GcpKeyManagementServiceClientConfig {
  GCP_KEY_MANAGEMENT_SERVICE_PROJECT: string;
  GCP_KEY_MANAGEMENT_SERVICE_LOCATION: string;
}

interface GcpKeyManagementServiceKeyConfig {
  GCP_KEY_MANAGEMENT_SERVICE_KEY_RING: string;
  GCP_KEY_MANAGEMENT_SERVICE_KEY: string;
}

// We plan to have different symmetric encryption keys for different purposes.
// If you create a new key, add it to the enum below and add the key ring/key name to getKeyManagementServiceKeyConfig.
export enum GcpKeyPurpose {
  PublicApiKeyEncryption = 'publicApiKeyEncryption',
}

export type GcpKeyPurposeConfigMapping = {
  GCP_KEY_MANAGEMENT_SERVICE_KEY_CONFIG: {
    [keyPurpose in GcpKeyPurpose]: GcpKeyManagementServiceKeyConfig;
  };
};

export interface ScimIntegrationTestConfig {
  SCIM_INTEGRATION_TEST_OKTA_ORG_URL: string;
  SCIM_INTEGRATION_TEST_OKTA_API_KEY: string;
  SCIM_INTEGRATION_TEST_OKTA_GROUP_ID: string;
  SCIM_INTEGRATION_TEST_USER_EMAIL: string;
}

export interface DocsConfig {
  APP_DOCS_PROXY_URL: string;
  APP_DOCS_PROXY_SECRET: string;
  SANITY_TOKEN: string;
}

export interface ModalConfig {
  MODAL_TOKEN_ID: string;
  MODAL_TOKEN_SECRET: string;
}

export interface BaseConfig {
  APPLICATION: AppType;

  // In development, the watershed username (i.e., the part before the @ sign in
  // their email) of the person whose dev environment this is.
  WS_USER: string | null;

  SEND_EMAILS: boolean;
  PRINT_DB_QUERIES: boolean;
  ENFORCE_QUERY_GOVERNANCE: QueryGovernance;
  ENFORCE_CROSS_DATABASE_TRANSACTION_GOVERNANCE: CrossDatabaseTransactionGovernance;
  ENFORCE_CROSS_REGION_READ_GOVERNANCE: CrossRegionReadGovernance;

  SLACK_BOT_TOKEN_WS_BOT?: string;
  SLACK_EXTERNAL_BOT_TOKEN?: string;
  SLACK_USER_UPLOAD_WORKFLOW_WEBHOOK_URL?: string;
  SLACK_MARKETPLACE_WEBHOOK_URL?: string;
  SLACK_PENDING_UPLOAD_WEBHOOK_URL?: string;
  SLACK_INTEGRATION_BOT_WEBHOOK_URL?: string;

  TRIPACTIONS_API_URL: string;
  TRIPACTIONS_API_URL_EU: string;

  README_JWT_SECRET: string;

  CONNECTION_ENCRYPTION_KEY: string;

  NEXT_HOSTNAME: string;

  DUST_API_KEY: string;

  REGION: Region;

  CACHE_WARMING_ENABLED?: boolean;
}

export type Config = BaseConfig &
  GoogleStorageConfig &
  PostgresConfig &
  QuickbooksConfig &
  MergeConfig &
  TemporalClientConfig &
  WorkOsConfig &
  HoneycombConfig &
  DuckDbServerConfig &
  DuckDbInProcessConfig &
  DuckDbTemporalConfig &
  SendgridConfig &
  SalesforceConfig &
  OneSchemaConfig &
  FlexibleReportsConfig &
  MapboxConfig &
  CrowdInConfig &
  DocsConfig &
  SentryConfig &
  HelpJuiceConfig &
  LangfuseConfig &
  CdpApiConfig &
  OpalConfig &
  GcpKeyManagementServiceClientConfig &
  GcpKeyPurposeConfigMapping &
  ScimIntegrationTestConfig &
  Partial<ModalConfig>;
